import { supabase } from "@/integrations/supabase/client";
import { invokeWithRetry } from './common/invokeWithRetry';

// Feature flags for notifications management edge function
// Progressive activation: GET → MARK → CREATE → DEVICE TOKENS
const FEATURE_FLAGS = {
  GET_NOTIFICATIONS: true,           // ✅ Phase 1: Read operations
  MARK_NOTIFICATION_READ: true,       // ✅ Phase 2: Mark operations
  MARK_ALL_NOTIFICATIONS_READ: true,  // ✅ Phase 2: Mark operations
  CREATE_NOTIFICATION: true,          // ✅ Phase 3: Create operations
  CREATE_NOTIFICATIONS_BULK: true,    // ✅ Phase 3: Create operations
  GET_DEVICE_TOKENS: true,           // ✅ Phase 4: Device token operations
  REGISTER_DEVICE_TOKEN: true,       // ✅ Phase 4: Device token operations
};

/**
 * Get user's unread notifications with booking enrichment
 */
export async function getNotifications(limit = 10) {
  if (!FEATURE_FLAGS.GET_NOTIFICATIONS) {
    // Fallback: direct database call
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: { message: "Not authenticated" } };

    const { data: notifData, error } = await supabase
      .from("notifications_sent")
      .select("*")
      .eq("user_id", user.id)
      .is("read_at", null)
      .order("sent_at", { ascending: false })
      .limit(limit);

    if (error) return { data: null, error };
    if (!notifData) return { data: [], error: null };

    // Enrich with bookings
    const bookingIds = notifData.filter(n => n.booking_id).map(n => n.booking_id);
    let bookingsMap = new Map();
    
    if (bookingIds.length > 0) {
      const { data: bookings } = await supabase
        .from("class_bookings")
        .select(`
          id,
          class_id,
          class_date,
          time,
          scheduled_classes!inner (
            id,
            name,
            time,
            instructor
          )
        `)
        .in("id", bookingIds);

      if (bookings) {
        bookings.forEach(b => bookingsMap.set(b.id, b));
      }
    }

    const enrichedData = notifData.map(notif => ({
      ...notif,
      booking: notif.booking_id ? bookingsMap.get(notif.booking_id) : undefined
    }));

    return { data: enrichedData, error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("notifications-management", {
    action: "get_notifications",
    limit,
  });

  if (authExpired) {
    return { data: [], error: null };
  }

  if (error) {
    console.error("[notificationsWrapper] get_notifications error:", error);
    return { data: null, error };
  }

  return { data: data.notifications, error: null };
}

/**
 * Mark a single notification as read
 */
export async function markNotificationRead(notificationId: string) {
  if (!FEATURE_FLAGS.MARK_NOTIFICATION_READ) {
    // Fallback: direct database call
    const { error } = await supabase
      .from("notifications_sent")
      .update({ read_at: new Date().toISOString() })
      .eq("id", notificationId);

    return { data: error ? null : { success: true }, error };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("notifications-management", {
    action: "mark_notification_read",
    notificationId,
  });

  if (authExpired) {
    // Session expirée : renvoyer un faux succès faisait afficher « Enregistré »
    // alors qu'aucune écriture n'a eu lieu.
    return { data: null, error: new Error('SESSION_EXPIRED') };
  }

  if (error) {
    console.error("[notificationsWrapper] mark_notification_read error:", error);
    return { data: null, error };
  }

  return { data, error: null };
}

/**
 * Mark all user's notifications as read
 */
export async function markAllNotificationsRead() {
  if (!FEATURE_FLAGS.MARK_ALL_NOTIFICATIONS_READ) {
    // Fallback: direct database call
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: { message: "Not authenticated" } };

    const { error } = await supabase
      .from("notifications_sent")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .is("read_at", null);

    return { data: error ? null : { success: true }, error };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("notifications-management", {
    action: "mark_all_notifications_read",
  });

  if (authExpired) {
    // Session expirée : renvoyer un faux succès faisait afficher « Enregistré »
    // alors qu'aucune écriture n'a eu lieu.
    return { data: null, error: new Error('SESSION_EXPIRED') };
  }

  if (error) {
    console.error("[notificationsWrapper] mark_all_notifications_read error:", error);
    return { data: null, error };
  }

  return { data, error: null };
}

/**
 * Create a single notification (requires admin/coach role)
 */
export async function createNotification(params: {
  targetUserId: string;
  type: string;
  payload?: any;
  scheduledAt?: string;
  gymId: string;
}) {
  if (!FEATURE_FLAGS.CREATE_NOTIFICATION) {
    // Fallback: direct database call
    const row = {
      user_id: params.targetUserId,
      type: params.type,
      payload: params.payload || {},
      scheduled_at: params.scheduledAt || new Date().toISOString(),
      gym_id: params.gymId,
    };

    const { error } = await supabase.from("notifications_plan").insert(row);
    return { data: error ? null : { success: true }, error };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("notifications-management", {
    action: "create_notification",
    ...params,
  });

  if (authExpired) {
    // Session expirée : renvoyer un faux succès faisait afficher « Enregistré »
    // alors qu'aucune écriture n'a eu lieu.
    return { data: null, error: new Error('SESSION_EXPIRED') };
  }

  if (error) {
    console.error("[notificationsWrapper] create_notification error:", error);
    return { data: null, error };
  }

  return { data, error: null };
}

/**
 * Create multiple notifications (bulk) (requires admin/coach role)
 */
export async function createNotificationsBulk(params: {
  userIds: string[];
  type: string;
  payload?: any;
  scheduledAt?: string;
  gymId: string;
}) {
  if (!FEATURE_FLAGS.CREATE_NOTIFICATIONS_BULK) {
    // Fallback: direct database call with chunking
    const scheduled = params.scheduledAt || new Date().toISOString();
    const rows = params.userIds.map(uid => ({
      user_id: uid,
      type: params.type,
      payload: params.payload || {},
      scheduled_at: scheduled,
      gym_id: params.gymId,
    }));

    let inserted = 0;
    for (let i = 0; i < rows.length; i += 200) {
      const chunk = rows.slice(i, i + 200);
      const { error, count } = await supabase
        .from("notifications_plan")
        .insert(chunk, { count: "exact" });

      if (error) return { data: null, error };
      inserted += count ?? chunk.length;
    }

    return { data: { success: true, inserted }, error: null };
  }

  // Edge function call
  // `timeoutMs` élargi : le lot n'est pas borné (`params.userIds` peut couvrir
  // toute la salle) et l'insertion se fait par paquets côté serveur. 30 s ne
  // suffisent pas sur les gros envois, et cet appel n'avait aucune limite
  // auparavant. Le lot n'est jamais renvoyé sur dépassement de délai
  // (`retryOnTimeout` vaut `false` par défaut) : une relance dupliquerait les
  // notifications, `notifications_plan` n'ayant pas de contrainte d'unicité.
  const { data, error, authExpired } = await invokeWithRetry(
    "notifications-management",
    {
      action: "create_notifications_bulk",
      ...params,
    },
    2,
    { timeoutMs: 60_000 }
  );

  if (authExpired) {
    // Session expirée : renvoyer un faux succès faisait afficher « Enregistré »
    // alors qu'aucune écriture n'a eu lieu.
    return { data: null, error: new Error('SESSION_EXPIRED') };
  }

  if (error) {
    console.error("[notificationsWrapper] create_notifications_bulk error:", error);
    return { data: null, error };
  }

  return { data, error: null };
}

export interface PaymentReminderBulkResult {
  success: boolean;
  inserted: number;
  ignored: number;
}

/** Envoie une relance d'impayé après revalidation serveur des destinataires. */
export async function createPaymentRemindersBulk(params: {
  userIds: string[];
  title: string;
  message: string;
  gymId: string;
}): Promise<{ data: PaymentReminderBulkResult | null; error: unknown | null }> {
  const { data, error, authExpired } = await invokeWithRetry(
    "notifications-management",
    { action: "create_payment_reminders_bulk", ...params },
    1,
    { timeoutMs: 60_000 }
  );

  if (authExpired) return { data: null, error: new Error("SESSION_EXPIRED") };
  if (error) {
    console.error("[notificationsWrapper] create_payment_reminders_bulk error:", error);
    return { data: null, error };
  }
  return { data: data as PaymentReminderBulkResult, error: null };
}

export interface DeviceToken {
  user_id: string;
  device_token: string;
  app_name: string | null;
  inserted_at: string;
}

/**
 * Get device tokens for the current user
 */
export async function getDeviceTokens() {
  if (!FEATURE_FLAGS.GET_DEVICE_TOKENS) {
    // Fallback: direct database call
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: { message: "Not authenticated" } };

    const { data, error } = await supabase
      .from("user_devices")
      .select("*")
      .eq("user_id", user.id)
      .order("inserted_at", { ascending: false });

    return { data: data || [], error };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("notifications-management", {
    action: "get_device_tokens",
  });

  if (authExpired) {
    return { data: [], error: null };
  }

  if (error) {
    console.error("[notificationsWrapper] get_device_tokens error:", error);
    return { data: [], error };
  }

  return { data: data.tokens || [], error: null };
}

/**
 * Register a device token for push notifications
 * @param deviceToken - The FCM device token
 * @param appName - The app name (e.g., "VB20TN", "MPILATES", "StriFit")
 */
export async function registerDeviceToken(deviceToken: string, appName?: string) {
  if (!FEATURE_FLAGS.REGISTER_DEVICE_TOKEN) {
    // Fallback: direct RPC call
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: { message: "Not authenticated" } };

    const { error } = await supabase.rpc('register_device_token', {
      uid: user.id,
      token: deviceToken,
      p_app_name: appName || null
    });

    return { data: error ? null : { success: true }, error };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("notifications-management", {
    action: "register_device_token",
    deviceToken,
    appName,
  });

  if (authExpired) {
    // Session expirée : renvoyer un faux succès faisait afficher « Enregistré »
    // alors qu'aucune écriture n'a eu lieu.
    return { data: null, error: new Error('SESSION_EXPIRED') };
  }

  if (error) {
    console.error("[notificationsWrapper] register_device_token error:", error);
    return { data: null, error };
  }

  return { data: data || { success: true }, error: null };
}
